import type { Allocation, PersonRef, PlanNode, RoleRef } from '@timelog/pm-shared';

/**
 * Pure allocation-list rules shared by the Resources popover and the
 * timeline's role picker.
 */

/**
 * Appends an allocation to a leaf's list — the timeline's on-bar add.
 *
 * It used to absorb the leaf's estimate into a seed row first, because the
 * write was about to delete it (BR-1). The budget now survives the write
 * (BR-28), so there is nothing to rescue: the list simply grows.
 */
export const withAppendedAllocation = (node: PlanNode, allocation: Allocation): Allocation[] => [
  ...node.allocations,
  allocation,
];

export type AllocationStackLayout = {
  /** Persons-first, capped — what the bar has room to show. */
  shown: Allocation[];
  /** How many allocations the +k chip stands for (0 = no chip). */
  overflow: number;
};

/**
 * The on-bar stack (v5, Area 2): resolved people come first, generic role
 * chips after, at most `max` items with a +k overflow chip.
 */
export const stackAllocations = (
  allocations: readonly Allocation[],
  max = 3,
): AllocationStackLayout => {
  const ordered = [...allocations].sort(
    (a, b) => Number(b.personRef !== undefined) - Number(a.personRef !== undefined),
  );
  const shown = ordered.slice(0, max);
  return { shown, overflow: ordered.length - shown.length };
};

/** The leaf's budget (CALC-1) — independent of what is allocated against it. */
export const taskBudgetHours = (node: PlanNode): number => node.estimatedHours ?? 0;

const sumHours = (allocations: readonly Allocation[]): number =>
  allocations.reduce((sum, allocation) => sum + allocation.hours, 0);

/** Hours still to place: the budget less what the lines already hold. */
export const remainingAgainst = (budgetHours: number, allocations: readonly Allocation[]): number =>
  Math.max(0, budgetHours - sumHours(allocations));

/**
 * A line being composed that has no role yet. A staffing line is never saved
 * without a role (D16), so until one is picked the line lives only in the
 * editor: hours and, if already chosen, the person. Picking a role — or a
 * person whose default role is on offer — turns it into an allocation.
 */
export type DraftLine = { hours: number; personRef?: PersonRef };

export type SeededLine =
  { kind: 'allocation'; allocation: Allocation } | { kind: 'draft'; draft: DraftLine };

/**
 * The popover's "Add resource" and Tab-from-the-last-hours-field (v8, Area 6):
 * ONE new line seeded with what is left of the captured budget. The very first
 * line keeps the task's own role and assignee so the common case — one person,
 * all the hours — is already right and BR-1 absorption never silently
 * un-prices the task; with no role to inherit it starts as a draft (D16 — no
 * "Not set" sentinel exists any more).
 *
 * Popover-only: the timeline keeps `withAppendedAllocation` (v5 stands).
 */
export const seedLine = (
  node: PlanNode,
  budgetHours: number,
  makeId: () => string = () => crypto.randomUUID(),
): SeededLine => {
  const hours = remainingAgainst(budgetHours, node.allocations);
  const first = node.allocations.length === 0;
  if (first && node.roleRef) {
    return {
      kind: 'allocation',
      allocation: {
        id: makeId(),
        roleRef: node.roleRef,
        ...(node.assignee ? { personRef: node.assignee } : {}),
        hours,
      },
    };
  }
  return {
    kind: 'draft',
    draft: { hours, ...(first && node.assignee ? { personRef: node.assignee } : {}) },
  };
};

/** A draft becomes an allocation the moment it has a role (D16). */
export const commitDraft = (
  draft: DraftLine,
  roleRef: RoleRef,
  makeId: () => string = () => crypto.randomUUID(),
): Allocation => ({
  id: makeId(),
  roleRef,
  ...(draft.personRef ? { personRef: draft.personRef } : {}),
  hours: draft.hours,
});

export type AllocationTotals = {
  /** Σ hours over every line, the draft included. */
  hours: number;
  /** Σ hours × the line's rate; the draft prices at nothing until it has a role. */
  budget: number;
  /** Hours beyond the task's budget; 0 while within it. */
  overHours: number;
};

/**
 * The footer figures, measured against the task's budget.
 *
 * Every line counts, the role-less draft included — deliberately: the
 * over-allocation signal has to appear while you are still typing. Under
 * budget the footer stays quiet (no green, no "N h remaining"): the remainder
 * is already doing that work by pre-filling the next line.
 */
export const allocationTotals = (
  allocations: readonly Allocation[],
  budgetHours: number,
  rateOf: (allocation: Allocation) => number,
  draftHours = 0,
): AllocationTotals => {
  const hours = sumHours(allocations) + draftHours;
  return {
    hours,
    budget: allocations.reduce((sum, allocation) => sum + allocation.hours * rateOf(allocation), 0),
    overHours: Math.max(0, hours - budgetHours),
  };
};

export type AllocationField = 'role' | 'person' | 'hours';
export type AllocationFocus = { row: number; field: AllocationField } | { row: 'add' };

const FOCUS_FIELDS: AllocationField[] = ['role', 'person', 'hours'];

/**
 * Enter/Tab order (v8): role → person → hours, then the next row's role, then
 * Add.
 *
 * `acceptsHours` lets the walk step over a line whose hours field is
 * read-only — a line naming nobody cannot hold hours, and landing focus in a
 * field that silently swallows typing is worse than not stopping there.
 * Defaults to "every row takes hours", so the plain call is unchanged.
 */
export const nextAllocationFocus = (
  current: { row: number; field: AllocationField },
  rowCount: number,
  direction: 1 | -1 = 1,
  acceptsHours: (row: number) => boolean = () => true,
): AllocationFocus | null => {
  let index = current.row * FOCUS_FIELDS.length + FOCUS_FIELDS.indexOf(current.field);
  // Bounded: at most one step per addressable field, so a row that accepts
  // nothing can never spin here.
  for (let step = 0; step < rowCount * FOCUS_FIELDS.length + 1; step += 1) {
    index += direction;
    if (index < 0) return null;
    if (index >= rowCount * FOCUS_FIELDS.length) return { row: 'add' };
    const row = Math.floor(index / FOCUS_FIELDS.length);
    const field = FOCUS_FIELDS[index % FOCUS_FIELDS.length]!;
    if (field !== 'hours' || acceptsHours(row)) return { row, field };
  }
  return { row: 'add' };
};
